import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { api } from '../../api/client';
import { useHojeReferencia } from '../../api/hooks/useSimClock';
import { isJustificativaAtiva, resolverStatusExibicao, toYmdLocal } from './justificativaEscopo';

type Item = {
  id: string;
  dataFalta: string;
  motivo: string;
  status: string;
  criadoEm: string;
  revisadoPor?: { nome: string } | null;
};

function parseMotivo(motivoStr: string) {
  const match = motivoStr.match(/^\[(.*?)\](.*)$/);
  if (!match) return { label: 'Geral', text: motivoStr };
  const cat = match[1];
  const map: Record<string, string> = {
    SAUDE: 'Saúde',
    CONSULTA: 'Consulta',
    FAMILIA: 'Familiar',
    OUTRO: 'Outros',
  };
  return { label: map[cat] || cat, text: (match[2] || '').trim() };
}

function fmtDate(d: string) {
  if (!d) return '—';
  try {
    const s = d.includes('T') ? d : `${d}T12:00:00`;
    return new Date(s).toLocaleDateString('pt-BR');
  } catch {
    return d;
  }
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  PENDENTE: { label: 'Em análise', color: '#d97706', bg: '#fffbeb' },
  VENCIDA: { label: 'Vencida', color: '#64748b', bg: '#f1f5f9' },
  ACEITA: { label: 'Abonada', color: '#16a34a', bg: '#f0fdf4' },
  RECUSADA: { label: 'Recusada', color: '#dc2626', bg: '#fef2f2' },
};

/** Bloco de justificativas para a ficha do aluno (histórico completo do aluno). */
export function JustificativasAlunoPanel({
  escolaId,
  alunoId,
}: {
  escolaId: string;
  alunoId: string;
}) {
  const { hojeStr } = useHojeReferencia();
  const { data = [], isLoading } = useQuery({
    queryKey: ['justificativas-aluno', escolaId, alunoId],
    queryFn: async () => {
      const { data: rows } = await api.get<Item[]>(
        `/escolas/${escolaId}/presencas-diarias/justificativas`,
        { params: { alunoId } },
      );
      return rows || [];
    },
    enabled: !!escolaId && !!alunoId,
  });

  const sorted = [...data].sort(
    (a, b) => toYmdLocal(b.dataFalta).localeCompare(toYmdLocal(a.dataFalta)),
  );

  return (
    <div
      style={{
        background: 'var(--bg-card, white)',
        borderRadius: 12,
        border: '1px solid var(--border-color, #e5e7eb)',
        padding: 16,
        marginTop: 14,
      }}
    >
      <h3 style={{ fontSize: '0.84rem', fontWeight: 700, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <FileText size={14} /> Justificativas de falta
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: '0.72rem', color: '#64748b' }}>
        Histórico do aluno. Após a data da falta, o item some da fila Ativas (escola e RESP).
      </p>

      {isLoading ? (
        <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: 0 }}>Carregando...</p>
      ) : sorted.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: 0 }}>
          Nenhuma justificativa registrada para este aluno.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map((j) => {
            const statusUi = resolverStatusExibicao(j, hojeStr);
            const st = STATUS_STYLE[statusUi] || STATUS_STYLE.PENDENTE;
            const parsed = parseMotivo(j.motivo || '');
            const ativa = isJustificativaAtiva(j, hojeStr);
            return (
              <div
                key={j.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: ativa ? '#fff' : '#f8fafc',
                  opacity: ativa ? 1 : 0.92,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: st.color,
                      background: st.bg,
                      padding: '2px 8px',
                      borderRadius: 6,
                    }}
                  >
                    {st.label}
                    {!ativa && statusUi !== 'VENCIDA' ? ' · histórico' : ''}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600 }}>
                    Falta: {fmtDate(j.dataFalta)}
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', marginBottom: 2 }}>
                  {parsed.label}
                </div>
                <div style={{ fontSize: '0.78rem', color: '#334155', lineHeight: 1.4 }}>
                  {parsed.text}
                </div>
                {j.revisadoPor?.nome && (
                  <div style={{ marginTop: 6, fontSize: '0.68rem', color: '#94a3b8' }}>
                    Revisado por {j.revisadoPor.nome}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
